import { HoverCard, Popover, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useAtomValue, useSetAtom } from "jotai";
import {
  Appointment,
  AppointmentStatus,
} from "../../../interfaces/services/Appointment";
import { WorkHour } from "../../../interfaces/services/Employee";
import {
  parseFixedISOStringToDate,
  toFixedISOString,
} from "../../../utils/dateUtils";
import {
  calendarRangeAtom,
  calendarStartDateAtom,
  getStatusColor,
} from "../../../pages/profile/Appointment/AppointmentsCalendarPage";
import appointmentService from "../../../services/api/appointmentService";
import financeService from "../../../services/api/financeService";
import { TransactionReq } from "../../../interfaces/services/Finance";
import { meAtom } from "../../../store/auth";
import { toast } from "react-toastify";
import AppointmentDetailsModal from "./AppointmentDetailsModal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/* ================= Props ================= */
type TimeStripWeeklyWithWorkHoursProps = {
  weekStartDate: Date;
  workHours: WorkHour[];
  minuteHeight?: number;
  showHourGrid?: boolean;
  gridEveryMinutes?: number;
  className?: string;
  appointments: Appointment[];
  groupPermissions: any;
  fetchAppointments: () => Promise<void>;
};

/* ================= Helpers ================= */
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const GUTTER_PX = 60;
const EN_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}
function addDays(d: Date, n: number): Date {
  const base = startOfLocalDay(d);
  return new Date(base.getTime() + n * MS_PER_DAY);
}
function parseHHmmssToMinutes(hhmmss: string): number {
  const parts = hhmmss.split(":");
  if (parts.length < 2) throw new Error(`Geçersiz saat: "${hhmmss}"`);
  const h = Number(parts[0]),
    m = Number(parts[1]),
    s = Number(parts[2] || "0");
  if (
    [h, m, s].some(Number.isNaN) ||
    h < 0 ||
    h > 23 ||
    m < 0 ||
    m > 59 ||
    s < 0 ||
    s > 59
  )
    throw new Error(`Geçersiz saat: "${hhmmss}"`);
  return h * 60 + m;
}
function minutesToHHmm(total: number): string {
  total = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const m = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(h)}:${pad(m)}`;
}
function hourNumberText(total: number): string {
  const norm = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(norm / 60);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(h)}:00`;
}
function parseDateToMinutes(date: Date): number {
  if (!(date instanceof Date) || Number.isNaN(date.getTime()))
    throw new Error("Geçersiz Date nesnesi");
  return date.getHours() * 60 + date.getMinutes();
}
function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}
function formatDayHeader(d: Date) {
  const wd = d.toLocaleDateString("tr-TR", { weekday: "short" });
  const dm = d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
  });
  return { wd, dm };
}
function findWorkHourForDate(
  workHours: WorkHour[],
  dayDate: Date,
): WorkHour | undefined {
  const name = EN_DAYS[dayDate.getDay()];
  return workHours.find((w) => w.day === name);
}
function getOpenSegmentsForDay(wh?: WorkHour): Array<[number, number]> {
  if (!wh) return [];
  const s = parseHHmmssToMinutes(wh.startTime);
  const e = parseHHmmssToMinutes(wh.endTime);
  if (e === s) return [];
  if (e > s) return [[s, e]];
  return [
    [0, e],
    [s, 1440],
  ];
}
function computeClosedSegmentsWithin(
  globalStart: number,
  globalEnd: number,
  openSegments: Array<[number, number]>,
): Array<[number, number]> {
  const clipped = openSegments
    .map(
      ([s, e]) =>
        [
          clamp(s, globalStart, globalEnd),
          clamp(e, globalStart, globalEnd),
        ] as [number, number],
    )
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0]);

  const merged: Array<[number, number]> = [];
  for (const seg of clipped) {
    if (!merged.length || seg[0] > merged[merged.length - 1][1])
      merged.push([...seg] as [number, number]);
    else
      merged[merged.length - 1][1] = Math.max(
        merged[merged.length - 1][1],
        seg[1],
      );
  }

  const closed: Array<[number, number]> = [];
  let cursor = globalStart;
  for (const [s, e] of merged) {
    if (s > cursor) closed.push([cursor, s]);
    cursor = Math.max(cursor, e);
  }
  if (cursor < globalEnd) closed.push([cursor, globalEnd]);
  if (!openSegments.length) return [[globalStart, globalEnd]];
  return closed;
}
function fmtTime(d: Date) {
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

/* ================= Group types & helpers ================= */
type WeeklyRawItem = {
  appt: Appointment;
  visibleStartMin: number;
  visibleEndMin: number;
  segStart: Date;
  segEnd: Date;
};

type WeeklyGroup = {
  items: WeeklyRawItem[];
  groupStartMin: number;
  groupEndMin: number;
};

function computeWeeklyGroupsForDay(
  appointments: Appointment[],
  day0: number,
  day1: number,
  slotStart: number,
  slotEnd: number,
): WeeklyGroup[] {
  const raw: WeeklyRawItem[] = [];

  for (const a of appointments) {
    try {
      const apptStart = parseFixedISOStringToDate(
        a.appointmentTime.appointmentStartDate,
      );
      const apptEnd = parseFixedISOStringToDate(
        a.appointmentTime.appointmentEndDate,
      );
      const segStartMs = Math.max(apptStart.getTime(), day0);
      const segEndMs = Math.min(apptEnd.getTime(), day1 - 1);
      if (segStartMs >= segEndMs) continue;

      const segStart = new Date(segStartMs);
      const segEnd = new Date(segEndMs);
      const visibleStartMin = clamp(
        parseDateToMinutes(segStart),
        slotStart,
        slotEnd,
      );
      const visibleEndMin = clamp(
        parseDateToMinutes(segEnd),
        slotStart,
        slotEnd,
      );
      if (visibleEndMin <= visibleStartMin) continue;

      raw.push({ appt: a, visibleStartMin, visibleEndMin, segStart, segEnd });
    } catch {
      /* skip */
    }
  }

  raw.sort((a, b) => a.visibleStartMin - b.visibleStartMin);

  const assigned = new Set<number>();
  const groups: WeeklyGroup[] = [];

  for (let i = 0; i < raw.length; i++) {
    if (assigned.has(i)) continue;
    const groupItems: WeeklyRawItem[] = [raw[i]];
    assigned.add(i);
    const queue = [i];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (let j = 0; j < raw.length; j++) {
        if (assigned.has(j)) continue;
        if (
          raw[cur].visibleStartMin < raw[j].visibleEndMin &&
          raw[cur].visibleEndMin > raw[j].visibleStartMin
        ) {
          groupItems.push(raw[j]);
          assigned.add(j);
          queue.push(j);
        }
      }
    }
    const groupStartMin = Math.min(...groupItems.map((x) => x.visibleStartMin));
    const groupEndMin = Math.max(...groupItems.map((x) => x.visibleEndMin));
    groups.push({ items: groupItems, groupStartMin, groupEndMin });
  }

  return groups;
}

/* ================= Component ================= */
export default function TimeStripWeekly({
  weekStartDate,
  workHours,
  minuteHeight = 2,
  showHourGrid = true,
  gridEveryMinutes = 30,
  className = "",
  appointments,
  groupPermissions,
  fetchAppointments,
}: TimeStripWeeklyWithWorkHoursProps) {
  const me = useAtomValue(meAtom);

  const [opened, { open, close }] = useDisclosure(false);
  const [selected, setSelected] = useState<Appointment | null>(null);

  const [now, setNow] = useState(new Date());
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const weekStartMs = startOfLocalDay(weekStartDate).getTime();
  const weekEndMs = weekStartMs + 7 * MS_PER_DAY;
  const isWithinWeek =
    now.getTime() >= weekStartMs && now.getTime() < weekEndMs;

  const canUpdate =
    groupPermissions.updateAppointmentStatus ||
    groupPermissions.updateAppointmentStatusAll ||
    groupPermissions.Receptionist;

  const setStartDate = useSetAtom(calendarStartDateAtom);
  const setRange = useSetAtom(calendarRangeAtom);
  const base = startOfLocalDay(weekStartDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(base, i));

  const nowRef = useRef<HTMLDivElement | null>(null);
  const hasAutoScrolledRef = useRef(false);

  // Haftanın global zaman penceresi — kapalı günler (startTime === endTime) hariç
  const openWorkHours = workHours.filter((w) => {
    try {
      const s = parseHHmmssToMinutes(w.startTime);
      const e = parseHHmmssToMinutes(w.endTime);
      return e !== s;
    } catch {
      return false;
    }
  });
  const starts = openWorkHours.map((w) => parseHHmmssToMinutes(w.startTime));
  const ends = openWorkHours.map((w) => parseHHmmssToMinutes(w.endTime));
  const globalStartMin = starts.length > 0 ? Math.min(...starts) : 600;
  const globalEndMin = ends.length > 0 ? Math.max(...ends) : 1320;
  const startMin = globalStartMin;
  const endAbs = globalEndMin;

  const duration = Math.max(0, endAbs - startMin);
  const heightPx = duration * minuteHeight;

  const firstFullHour = Math.ceil(startMin / 60) * 60;
  const hourMarks: number[] = [];
  for (let t = firstFullHour; t <= endAbs; t += 60)
    if (t !== startMin) hourMarks.push(t);
  const endIsOnHour = endAbs % 60 === 0;

  const minorGrid: number[] = [];
  if (gridEveryMinutes && gridEveryMinutes > 0) {
    const firstMinor =
      Math.ceil(startMin / gridEveryMinutes) * gridEveryMinutes;
    for (let t = firstMinor; t < endAbs; t += gridEveryMinutes) {
      if (!(showHourGrid && t % 60 === 0)) minorGrid.push(t);
    }
  }

  const showNow =
    isWithinWeek && nowMinutes >= startMin && nowMinutes <= endAbs;

  const topOf = (t: number) => (t - startMin) * minuteHeight;
  const heightOf = (mins: number) => mins * minuteHeight;

  /* ====== Popup state ====== */
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);

  /* ====== Per-day overlap groups ====== */
  const weeklyGroups = useMemo<WeeklyGroup[][]>(() => {
    const b = startOfLocalDay(weekStartDate);
    return Array.from({ length: 7 }, (_, i) => {
      const day = addDays(b, i);
      const d0 = startOfLocalDay(day).getTime();
      const d1 = d0 + MS_PER_DAY;
      return computeWeeklyGroupsForDay(appointments, d0, d1, startMin, endAbs);
    });
  }, [appointments, weekStartDate, startMin, endAbs]);

  function RightGridOverlay() {
    return (
      <div className="pointer-events-none absolute inset-0 z-0">
        {showHourGrid &&
          hourMarks.map((t) => (
            <div
              key={`hline-${t}`}
              className="absolute left-0 right-0 h-px bg-background-lightAlt2 dark:bg-background-darkAlt2"
              style={{ top: `${topOf(t)}px` }}
            />
          ))}
        {minorGrid.map((t) => (
          <div
            key={`mline-${t}`}
            className="absolute left-0 right-0 h-px bg-background-lightAlt2/30 dark:bg-background-darkAlt2/50"
            style={{ top: `${topOf(t)}px` }}
          />
        ))}
      </div>
    );
  }

  function LeftTimeGutter() {
    return (
      <div
        className="relative shrink-0 border-r border-background-lightAlt2 dark:border-background-darkAlt2 px-3"
        style={{ width: GUTTER_PX, height: heightPx }}
      >
        <div className="absolute left-3 -translate-y-1/2 text-xs font-medium text-gray-600 dark:text-gray-300">
          {minutesToHHmm(startMin)}
        </div>
        {hourMarks
          .filter((t) => (endIsOnHour ? t <= endAbs : t < endAbs))
          .map((t) => (
            <div
              key={`hour-${t}`}
              className="absolute left-3 -translate-y-1/2 text-xs font-medium text-gray-600 dark:text-gray-300"
              style={{ top: `${topOf(t)}px` }}
              title={minutesToHHmm(t)}
            >
              {hourNumberText(t)}
            </div>
          ))}
        {!endIsOnHour && (
          <div
            className="absolute left-3 -translate-y-1/2 text-xs font-medium text-gray-600 dark:text-gray-300"
            style={{ top: `${topOf(endAbs)}px` }}
          >
            {minutesToHHmm(endAbs)}
          </div>
        )}
      </div>
    );
  }

  /* ===== Actions ===== */
  const changeStatus = useCallback(
    async (a: Appointment, status: AppointmentStatus) => {
      try {
        await appointmentService.changestatus(a.code, status);

        if (status === "Completed") {
          const price = a.serviceSnapshot?.price || 0;
          const _date = new Date();
          const transaction: TransactionReq = {
            salonCode: me?.salonCode!,
            type: "Income",
            category: "Appointment",
            status: true,
            title: `Oto Randevu Ödemesi`,
            amount: price,
            date: toFixedISOString(_date),
            note: `SalonCepte tarafından otomatik olarak oluşturuldu`,
            employeeCode: a.employeeCode,
            employeeSnapshot: {
              name: me?.name!,
              lastname: me?.lastname!,
            },
          };
          await financeService.createTransaction(transaction);
          toast.success("Randevu Güncellendi ve Gelir kaydı oluşturuldu", {
            toastId: "change-status-success-with-income",
          });
        } else {
          toast.success("Randevu Güncellendi", {
            toastId: "change-status-success",
          });
        }
        fetchAppointments();
      } catch (error) {
        toast.error("Randevu Güncellenemedi", {
          toastId: "change-status-error",
        });
        console.error(error);
      }
    },
    [me, fetchAppointments],
  );

  /* ===== Closed blocks ===== */
  function renderClosedBlocksForDay(day: Date) {
    const wh = findWorkHourForDate(workHours, day);
    const openSegments = getOpenSegmentsForDay(wh);
    const closedSegments = computeClosedSegmentsWithin(
      startMin,
      endAbs,
      openSegments,
    );
    if (!closedSegments.length) return null;

    return closedSegments.map(([s, e], i) => {
      const top = topOf(s);
      const height = heightOf(e - s);
      return (
        <HoverCard key={`closed-${i}-${s}-${e}`} width={280} shadow="md">
          <HoverCard.Target>
            <div
              className="absolute left-0 right-0 z-[5] rounded-[2px] border border-zinc-400/30 dark:border-zinc-600/30"
              style={{
                top: `${top}px`,
                height: `${height}px`,
                backgroundImage:
                  "repeating-linear-gradient(45deg, rgba(120,120,120,0.15) 0, rgba(120,120,120,0.15) 6px, transparent 6px, transparent 12px)",
                backgroundColor: "rgba(120,120,120,0.08)",
              }}
            />
          </HoverCard.Target>
          <HoverCard.Dropdown>
            <Text size="sm">Hizmet verilmiyor</Text>
          </HoverCard.Dropdown>
        </HoverCard>
      );
    });
  }

  /* ===== Tek randevu kartı (haftalık) ===== */
  function renderSingleWeekly(item: WeeklyRawItem) {
    const { appt: a, visibleStartMin, visibleEndMin, segStart, segEnd } = item;
    const top = topOf(visibleStartMin);
    const height = heightOf(visibleEndMin - visibleStartMin);
    const isCompact = height < 40;

    const startLabel = fmtTime(segStart);
    const endLabel = fmtTime(segEnd);

    return (
      <div
        key={`apt-${a.code}`}
        className="absolute z-10 rounded-md cursor-pointer shadow-sm"
        style={{
          top: `${top}px`,
          height: `${height}px`,
          left: "1px",
          right: "1px",
          background: getStatusColor(a.status),
        }}
        title={`${minutesToHHmm(visibleStartMin)} - ${minutesToHHmm(
          visibleEndMin,
        )}${
          a.customerSnapshot
            ? ` • ${a.customerSnapshot.name} ${a.customerSnapshot.lastname}`
            : ""
        }`}
        onClick={() => {
          setSelected(a);
          open();
        }}
      >
        <div className="h-full w-full flex flex-col justify-center px-1.5 text-white overflow-hidden">
          <span className="font-bold text-[10px] whitespace-nowrap leading-tight">
            {startLabel}–{endLabel}
          </span>
          {!isCompact && a.customerSnapshot && (
            <span className="text-[10px] opacity-85 truncate leading-tight">
              {a.customerSnapshot.name} {a.customerSnapshot.lastname}
            </span>
          )}
        </div>
      </div>
    );
  }

  /* ===== Çakışan randevular — merged blok + popover (haftalık) ===== */
  function renderMergedWeekly(group: WeeklyGroup, idx: number) {
    const { items, groupStartMin, groupEndMin } = group;
    const top = topOf(groupStartMin);
    const height = heightOf(groupEndMin - groupStartMin);
    const isCompact = height < 40;

    const groupKey = items
      .map((i) => i.appt.code)
      .sort()
      .join("-");
    const isOpen = expandedGroupKey === groupKey;

    const earliest = items.reduce((a, b) =>
      a.visibleStartMin <= b.visibleStartMin ? a : b,
    );
    const latest = items.reduce((a, b) =>
      a.visibleEndMin >= b.visibleEndMin ? a : b,
    );
    const startLabel = fmtTime(earliest.segStart);
    const endLabel = fmtTime(latest.segEnd);

    return (
      <Popover
        key={`grp-${idx}`}
        opened={isOpen}
        onChange={(o) => setExpandedGroupKey(o ? groupKey : null)}
        position="top-start"
        withArrow
        shadow="md"
        closeOnClickOutside
        zIndex={200}
        middlewares={{ flip: { fallbackPlacements: ["bottom-start"] } }}
      >
        <Popover.Target>
          <div
            className="absolute z-10 rounded-md cursor-pointer shadow-sm overflow-hidden"
            style={{
              top: `${top}px`,
              height: `${height}px`,
              left: "1px",
              right: "1px",
            }}
            onClick={() => setExpandedGroupKey(isOpen ? null : groupKey)}
          >
            {/* Sol durum şeridi */}
            <div className="absolute left-0 top-0 bottom-0 w-1 flex flex-col z-10">
              {items.map((item, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    backgroundColor: getStatusColor(item.appt.status),
                  }}
                />
              ))}
            </div>

            {/* Arka plan */}
            <div className="absolute inset-0 bg-slate-700 dark:bg-slate-800" />

            {/* İçerik */}
            <div className="relative ml-1 px-1.5 py-0.5 h-full flex flex-col justify-center">
              <div className="flex items-center gap-1">
                <span className="text-white font-bold text-[10px] whitespace-nowrap leading-tight">
                  {startLabel}–{endLabel}
                </span>
                <span className="text-[9px] font-bold text-white bg-white/25 px-1 py-0.5 rounded-full leading-none">
                  {items.length}
                </span>
              </div>
              {!isCompact && (
                <div className="flex gap-0.5 mt-0.5">
                  {items.slice(0, 5).map((item, i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        backgroundColor: getStatusColor(item.appt.status),
                      }}
                    />
                  ))}
                  {items.length > 5 && (
                    <span className="text-white/40 text-[8px] self-center">
                      +{items.length - 5}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </Popover.Target>

        <Popover.Dropdown p="xs" style={{ minWidth: 220, maxWidth: 300 }}>
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-1 mb-2">
            {items.length} randevu · {startLabel}–{endLabel}
          </p>
          <div className="flex flex-col gap-0.5">
            {items
              .slice()
              .sort((a, b) => a.visibleStartMin - b.visibleStartMin)
              .map((item, i) => {
                const s = fmtTime(item.segStart);
                const e = fmtTime(item.segEnd);
                return (
                  <div
                    key={i}
                    className="flex items-start gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                    onClick={() => {
                      setExpandedGroupKey(null);
                      setSelected(item.appt);
                      open();
                    }}
                  >
                    <div
                      className="w-2.5 h-2.5 rounded-full shrink-0 mt-0.5"
                      style={{
                        backgroundColor: getStatusColor(item.appt.status),
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                        {s}–{e}
                        {item.appt.serviceSnapshot && (
                          <span className="font-normal text-gray-500 dark:text-gray-400 ml-1.5">
                            {item.appt.serviceSnapshot.name}
                          </span>
                        )}
                      </div>
                      {item.appt.customerSnapshot ? (
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                          {item.appt.customerSnapshot.name}{" "}
                          {item.appt.customerSnapshot.lastname}
                        </div>
                      ) : (
                        <div className="text-[11px] text-gray-400 italic">
                          Müşteri belirtilmemiş
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </Popover.Dropdown>
      </Popover>
    );
  }

  function CurrentTimeIndicator() {
    if (!showNow) return null;
    const top = (nowMinutes - startMin) * minuteHeight;
    return (
      <div
        ref={nowRef}
        className="absolute left-0 right-0 z-20 select-none"
        style={{ top }}
      >
        <div className="absolute -left-3 -translate-y-1/2 top-1/2">
          <div className="w-0 h-0 border-y-6 border-y-transparent border-r-6 border-r-rose-500 dark:border-r-rose-400" />
        </div>
        <div className="h-[2px] w-full bg-rose-500/90 dark:bg-rose-400/90 shadow-[0_0_0_1px_rgba(0,0,0,.05)]" />
        <div className="absolute -translate-y-1/2 top-1/2 left-0">
          <span className="block w-2.5 h-2.5 rounded-full bg-rose-500 dark:bg-rose-400" />
          <span className="block w-2.5 h-2.5 rounded-full bg-rose-500/40 dark:bg-rose-400/40 animate-ping absolute inset-0" />
        </div>
      </div>
    );
  }

  useEffect(() => {
    if (!isWithinWeek) return;
    const id = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => clearInterval(id);
  }, [isWithinWeek]);

  useEffect(() => {
    hasAutoScrolledRef.current = false;
  }, [weekStartDate, startMin, endAbs]);

  useEffect(() => {
    if (!showNow || hasAutoScrolledRef.current) return;
    requestAnimationFrame(() => {
      nowRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      hasAutoScrolledRef.current = true;
    });
  }, [showNow]);

  /* ===== Render ===== */
  return (
    <>
      <div
        className={[
          "overflow-hidden min-w-[800px] rounded-2xl border border-background-lightAlt2 dark:border-background-darkAlt2 bg-background-light dark:bg-background-dark shadow-sm w-full",
          className,
        ].join(" ")}
      >
        {/* Header */}
        <div className="relative z-10 flex border-b border-background-lightAlt2 dark:border-background-darkAlt2 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur">
          <div className="shrink-0" style={{ width: GUTTER_PX }} />
          <div className="grid grid-cols-7 w-full">
            {days.map((d, i) => {
              const { wd, dm } = formatDayHeader(d);
              return (
                <div
                  key={`hdr-${i}`}
                  className="px-3 py-3 text-gray-900 dark:text-gray-100 border-r last:border-r-0 border-background-lightAlt2 dark:border-background-darkAlt2"
                >
                  <div className="text-[11px] uppercase tracking-wide text-gray-500">
                    {wd}
                  </div>
                  <div className="text-sm font-semibold">{dm}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex py-4 mb-8">
          <LeftTimeGutter />

          <div className="relative w-full" style={{ height: heightPx }}>
            <RightGridOverlay />
            <CurrentTimeIndicator />
            <div className="grid grid-cols-7 w-full h-full relative z-0">
              {days.map((day, colIdx) => (
                <div
                  key={`col-${colIdx}`}
                  className="relative border-r last:border-r-0 border-background-lightAlt2 dark:border-background-darkAlt2 cursor-pointer hover:bg-background-lightAlt1 dark:hover:bg-background-darkAlt1"
                  onDoubleClick={() => {
                    setStartDate(day);
                    setRange("daily");
                  }}
                >
                  {/* Kapalı bloklar */}
                  {renderClosedBlocksForDay(day)}

                  {/* Randevular */}
                  {weeklyGroups[colIdx].map((group, idx) =>
                    group.items.length === 1
                      ? renderSingleWeekly(group.items[0])
                      : renderMergedWeekly(group, idx),
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <AppointmentDetailsModal
          opened={opened}
          onClose={() => {
            close();
            setSelected(null);
          }}
          a={selected}
          canAct={{ canUpdate }}
          changeStatus={changeStatus}
        />
      )}
    </>
  );
}
